/**
 * AdminConfiguration.jsx — Super Admin only.
 *
 * Implements the "Configuration" section defined in
 * /app/memory/MISEONE_CONFIGURATION_ARCHITECTURE.md.
 *
 * Four sub-tabs, none of which are exposed to Corporate/Restaurant users:
 *   • Platform Default       — the shared baseline for MiseOne
 *   • Company Configuration  — per-company overrides
 *   • Restaurant Configuration — per-restaurant overrides
 *   • Audit Log              — chronological history of all changes
 *
 * Phase 1 gates only these 5 feature flags:
 *   ai_delivery_reader, barcode_scanner, invoice_module,
 *   waste_report, hot_holding_checks
 * Core modules (Workflow, Weekly Menu, ...) are NOT gated.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronRight, ShieldCheck, History,
         Building2, Store, Globe } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useResolvedConfig } from "@/context/ResolvedConfigContext";
import i18n from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
    { key: "ai_delivery_reader",
       get label() { return i18n.t("admin.l.AI_Delivery_Reader", "AI Delivery Reader"); },
       get help() { return i18n.t("admin.l.Extract_line_items_from_delivery_note_photos_PDFs_with_Gemini_AI", "Extract line-items from delivery-note photos/PDFs with Gemini AI."); } },
    { key: "barcode_scanner",
       get label() { return i18n.t("admin.l.Barcode_Scanner", "Barcode Scanner"); },
       get help() { return i18n.t("admin.l.Scan_barcodes_in_Inventory_and_Deliveries_workflows", "Scan barcodes in Inventory and Deliveries workflows."); } },
    { key: "invoice_module",
       get label() { return i18n.t("admin.l.Invoice_Module_billing", "Invoice Module (billing)"); },
       get help() { return i18n.t("admin.l.Whether_this_Company_is_billed_through_the_MiseOne_billing_system_Comm", "Whether this Company is billed through the MiseOne billing system. Commercial only."); } },
    { key: "waste_report",
       get label() { return i18n.t("admin.l.Waste_Report", "Waste Report"); },
       get help() { return i18n.t("admin.l.Waste_report_CSV_PDF_export_in_Reports", "Waste report + CSV/PDF export in Reports."); } },
    { key: "hot_holding_checks",
       get label() { return i18n.t("admin.l.Hot_Holding_Checks", "Hot Holding Checks"); },
       get help() { return i18n.t("admin.l.Hot_Holding_temperature_logs_in_Daily_Checks", "Hot Holding temperature logs in Daily Checks."); } },
];

// ============================================================
// Utility: side-by-side effective table
// ============================================================
function EffectiveTable({ platform, companyOverride, restaurantOverride }) {
    const rowFor = (key) => {
        const p = platform?.features?.[key];
        const c = companyOverride?.features?.[key];
        const r = restaurantOverride?.features?.[key];
        const eff = r ?? c ?? p ?? true;
        const src = r !== undefined ? "restaurant"
                     : c !== undefined ? "company"
                     : "platform";
        return { key, p, c, r, eff, src };
    };
    return (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="config-effective-table">
                <thead className="bg-neutral-50 dark:bg-neutral-900">
                    <tr>
                        <th className="text-left px-4 py-2">{i18n.t("admin.l.Feature", "Feature")}</th>
                        <th className="px-3 py-2">{i18n.t("admin.l.Platform", "Platform")}</th>
                        <th className="px-3 py-2">{i18n.t("admin.l.Company", "Company")}</th>
                        <th className="px-3 py-2">{i18n.t("admin.l.Restaurant", "Restaurant")}</th>
                        <th className="px-3 py-2">{i18n.t("admin.l.Effective", "Effective")}</th>
                    </tr>
                </thead>
                <tbody>
                    {FEATURES.map((f) => {
                        const row = rowFor(f.key);
                        const badge = (val) =>
                            val === undefined || val === null
                                ? <span className="text-neutral-400">—</span>
                                : (val
                                    ? <span className="text-emerald-600 font-medium">ON</span>
                                    : <span className="text-red-600 font-medium">OFF</span>);
                        return (
                            <tr key={f.key} className="border-t border-neutral-200 dark:border-neutral-800">
                                <td className="px-4 py-2">
                                    <div>{f.label}</div>
                                    <div className="text-xs text-neutral-500">{f.help}</div>
                                </td>
                                <td className="text-center">{badge(row.p)}</td>
                                <td className="text-center">{badge(row.c)}</td>
                                <td className="text-center">{badge(row.r)}</td>
                                <td className="text-center">
                                    {badge(row.eff)}
                                    <div className="text-[10px] text-neutral-500">{row.src}</div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ============================================================
// Sub-tab 1: Platform Default
// ============================================================
function PlatformDefaultPanel() {
    const [doc, setDoc] = useState(null);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { refresh: refreshResolved } = useResolvedConfig();

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/config/platform`,
                                                { withCredentials: true });
            setDoc(data);
            setDraft(data.features || {});
        } catch (e) {
            toast.error(i18n.t("admin.l.Could_not_load_Platform_Default", "Could not load Platform Default."));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const changed = useMemo(() => {
        if (!doc) return false;
        return FEATURES.some((f) => Boolean(doc.features?.[f.key])
                                     !== Boolean(draft?.[f.key]));
    }, [doc, draft]);

    const doSave = async () => {
        setSaving(true);
        try {
            await axios.patch(`${API}/admin/config/platform`,
                                { features: draft },
                                { withCredentials: true });
            toast.success(i18n.t("admin.l.Platform_Default_saved", "Platform Default saved."));
            setConfirmOpen(false);
            await load();
            await refreshResolved();
        } catch (e) {
            toast.error(i18n.t("admin.l.Save_failed_no_changes_applied", "Save failed — no changes applied."));
        } finally {
            setSaving(false);
        }
    };

    if (!doc) return <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />;
    return (
        <div className="space-y-4">
            <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-900 dark:text-amber-200">
                <span dangerouslySetInnerHTML={{ __html: i18n.t("admin.cfg.platformNote") }} />
            </div>
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
                {FEATURES.map((f) => (
                    <div key={f.key} className="flex items-center justify-between px-4 py-3">
                        <div className="pr-6">
                            <div className="font-medium">{f.label}</div>
                            <div className="text-xs text-neutral-500">{f.help}</div>
                        </div>
                        <Switch
                            data-testid={`platform-flag-${f.key}`}
                            checked={Boolean(draft?.[f.key])}
                            onCheckedChange={(v) =>
                                setDraft((d) => ({ ...d, [f.key]: Boolean(v) }))}
                        />
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" onClick={() => setDraft(doc.features || {})}
                    disabled={!changed}>
                    Discard
                </Button>
                <Button onClick={() => setConfirmOpen(true)}
                    disabled={!changed}
                    data-testid="platform-save-btn">
                    {i18n.t("admin.l.Save_Platform_Default", "Save Platform Default")}
                </Button>
            </div>
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.t("admin.l.Confirm_Platform_Default_change", "Confirm Platform Default change")}</DialogTitle>
                        <DialogDescription>
                            {i18n.t("admin.cfg.confirmDesc", "This change affects every Company currently inheriting the Platform Default. Companies with their own Override remain unaffected.")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
                            {i18n.t("admin.l.Cancel", "Cancel")}
                        </Button>
                        <Button onClick={doSave} disabled={saving}
                            data-testid="platform-save-confirm">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : i18n.t("admin.l.Apply", "Apply")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ============================================================
// Sub-tab 2: Company Configuration
// ============================================================
function CompanyOverridePanel({ companyId, onClose }) {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const { refresh: refreshResolved } = useResolvedConfig();

    const load = useCallback(async () => {
        const { data } = await axios.get(
            `${API}/admin/config/company/${companyId}`, { withCredentials: true });
        setData(data);
        setDraft({ ...(data.override?.features || {}) });
    }, [companyId]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />;

    const doSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/admin/config/company/${companyId}`,
                              { features: draft }, { withCredentials: true });
            toast.success(i18n.t("admin.l.Company_Override_saved", "Company Override saved."));
            await load();
            await refreshResolved();
        } catch (e) {
            toast.error(i18n.t("admin.l.Save_failed_no_changes_applied", "Save failed — no changes applied."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 p-3 text-sm text-blue-900 dark:text-blue-200">
                <span dangerouslySetInnerHTML={{ __html: i18n.t("admin.cfg.companyNote") }} />
            </div>
            <EffectiveTable
                platform={data.platform}
                companyOverride={{ features: draft }}
                restaurantOverride={null}
            />
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
                {FEATURES.map((f) => (
                    <div key={f.key} className="flex items-center justify-between px-4 py-3">
                        <div className="pr-6">
                            <div className="font-medium">{f.label}</div>
                            <div className="text-xs text-neutral-500">
                                Platform: <strong>{data.platform.features[f.key] ? "ON" : "OFF"}</strong>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-xs text-neutral-500">
                                {draft[f.key] === undefined ? "inherit" : "override"}
                            </span>
                            <Switch
                                data-testid={`company-flag-${f.key}`}
                                checked={draft[f.key] === undefined
                                    ? Boolean(data.platform.features[f.key])
                                    : Boolean(draft[f.key])}
                                onCheckedChange={(v) => setDraft((d) => ({ ...d, [f.key]: Boolean(v) }))}
                            />
                            {draft[f.key] !== undefined && (
                                <Button size="sm" variant="ghost"
                                    onClick={() => setDraft((d) => {
                                        const n = { ...d }; delete n[f.key]; return n;
                                    })}>
                                    Inherit
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-between gap-2">
                <span />
                <div className="flex gap-2">
                    {onClose && <Button variant="ghost" onClick={onClose}>{i18n.t("admin.l.Close", "Close")}</Button>}
                    <Button onClick={doSave} disabled={saving}
                        data-testid="company-save-btn">
                        {i18n.t("admin.l.Save_Company_Override", "Save Company Override")}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function CompanyConfigList() {
    const [companies, setCompanies] = useState([]);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/companies`, { withCredentials: true })
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data
                    : (r.data.items || []);
                setCompanies(list);
            }).catch(() => setCompanies([]));
    }, []);
    if (selected) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm"
                        onClick={() => setSelected(null)}>{i18n.t("admin.l.Back_to_list", "← Back to list")}</Button>
                    <span className="font-semibold">{selected.name}</span>
                </div>
                <CompanyOverridePanel companyId={selected.id}
                    onClose={() => setSelected(null)} />
            </div>
        );
    }
    return (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
            {companies.map((c) => (
                <button key={c.id}
                    onClick={() => setSelected(c)}
                    data-testid={`open-company-config-${c.id}`}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-900 text-left">
                    <div className="flex items-center gap-3">
                        <Building2 className="w-4 h-4 text-neutral-500" />
                        <span className="font-medium">{c.name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-neutral-400" />
                </button>
            ))}
            {companies.length === 0 && (
                <div className="px-4 py-6 text-sm text-neutral-500">{i18n.t("admin.l.No_companies_yet", "No companies yet.")}</div>
            )}
        </div>
    );
}

// ============================================================
// Sub-tab 3: Restaurant Configuration
// ============================================================
function RestaurantOverridePanel({ restaurantId, onClose }) {
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const { refresh: refreshResolved } = useResolvedConfig();

    const load = useCallback(async () => {
        const { data } = await axios.get(
            `${API}/admin/config/restaurant/${restaurantId}`,
            { withCredentials: true });
        setData(data);
        setDraft({ ...(data.override?.features || {}) });
    }, [restaurantId]);
    useEffect(() => { load(); }, [load]);

    if (!data) return <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />;

    const parentFor = (key) => {
        const c = data.company_override?.features?.[key];
        return c !== undefined ? c : data.platform.features[key];
    };

    const doSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/admin/config/restaurant/${restaurantId}`,
                              { features: draft }, { withCredentials: true });
            toast.success(i18n.t("admin.l.Restaurant_Override_saved", "Restaurant Override saved."));
            await load();
            await refreshResolved();
        } catch (e) {
            toast.error(i18n.t("admin.l.Save_failed_no_changes_applied", "Save failed — no changes applied."));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="rounded-md bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 p-3 text-sm text-purple-900 dark:text-purple-200">
                <span dangerouslySetInnerHTML={{ __html: i18n.t("admin.cfg.restaurantNote") }} />
            </div>
            <EffectiveTable
                platform={data.platform}
                companyOverride={data.company_override}
                restaurantOverride={{ features: draft }}
            />
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
                {FEATURES.map((f) => {
                    const parent = parentFor(f.key);
                    return (
                        <div key={f.key} className="flex items-center justify-between px-4 py-3">
                            <div className="pr-6">
                                <div className="font-medium">{f.label}</div>
                                <div className="text-xs text-neutral-500">
                                    Inherited: <strong>{parent ? "ON" : "OFF"}</strong>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-neutral-500">
                                    {draft[f.key] === undefined ? "inherit" : "override"}
                                </span>
                                <Switch
                                    data-testid={`restaurant-flag-${f.key}`}
                                    checked={draft[f.key] === undefined
                                        ? Boolean(parent)
                                        : Boolean(draft[f.key])}
                                    onCheckedChange={(v) => setDraft((d) => ({ ...d, [f.key]: Boolean(v) }))}
                                />
                                {draft[f.key] !== undefined && (
                                    <Button size="sm" variant="ghost"
                                        onClick={() => setDraft((d) => {
                                            const n = { ...d }; delete n[f.key]; return n;
                                        })}>
                                        Inherit
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center justify-between gap-2">
                <span />
                <div className="flex gap-2">
                    {onClose && <Button variant="ghost" onClick={onClose}>{i18n.t("admin.l.Close", "Close")}</Button>}
                    <Button onClick={doSave} disabled={saving}
                        data-testid="restaurant-save-btn">
                        {i18n.t("admin.l.Save_Restaurant_Override", "Save Restaurant Override")}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function RestaurantConfigList() {
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/restaurants`, { withCredentials: true })
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data
                    : (r.data.items || []);
                setRows(list);
            }).catch(() => setRows([]));
    }, []);
    if (selected) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm"
                        onClick={() => setSelected(null)}>{i18n.t("admin.l.Back_to_list", "← Back to list")}</Button>
                    <span className="font-semibold">{selected.name}</span>
                </div>
                <RestaurantOverridePanel restaurantId={selected.id}
                    onClose={() => setSelected(null)} />
            </div>
        );
    }
    return (
        <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
            {rows.map((r) => (
                <button key={r.id}
                    onClick={() => setSelected(r)}
                    data-testid={`open-restaurant-config-${r.id}`}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-900 text-left">
                    <div className="flex items-center gap-3">
                        <Store className="w-4 h-4 text-neutral-500" />
                        <span className="font-medium">{r.name}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-neutral-400" />
                </button>
            ))}
            {rows.length === 0 && (
                <div className="px-4 py-6 text-sm text-neutral-500">{i18n.t("admin.l.No_restaurants_yet", "No restaurants yet.")}</div>
            )}
        </div>
    );
}

// ============================================================
// Sub-tab 4: Audit Log
// ============================================================
function AuditLogPanel() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/config/audit?limit=200`,
                { withCredentials: true });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between text-sm text-neutral-500">
                <span>Total events: {total}</span>
                <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                    data-testid="audit-refresh">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : i18n.t("admin.l.Refresh", "Refresh")}
                </Button>
            </div>
            <div className="border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 dark:bg-neutral-900">
                        <tr>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.When", "When")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Actor", "Actor")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Scope", "Scope")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Setting", "Setting")}</th>
                            <th className="text-left px-3 py-2">{i18n.t("admin.l.Change", "Change")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((x) => (
                            <tr key={x.id} className="border-t border-neutral-200 dark:border-neutral-800">
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-neutral-500">
                                    {(x.timestamp || "").slice(0, 19).replace("T", " ")}
                                </td>
                                <td className="px-3 py-2 text-xs">{x.actor_email}</td>
                                <td className="px-3 py-2 text-xs">
                                    <span className="px-2 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800">
                                        {x.scope}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{x.setting_path}</td>
                                <td className="px-3 py-2 text-xs">
                                    <code>{String(x.previous_value)}</code>
                                    {" → "}
                                    <code>{String(x.new_value)}</code>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && (
                            <tr><td colSpan="5" className="px-4 py-6 text-sm text-neutral-500">
                                {i18n.t("admin.l.No_configuration_changes_recorded_yet", "No configuration changes recorded yet.")}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================
// Main tab component
// ============================================================
const SUB_TABS = [
    { key: "platform", get label() { return i18n.t("admin.l.Platform_Default", "Platform Default"); }, icon: Globe },
    { key: "company", get label() { return i18n.t("admin.l.Company_Configuration", "Company Configuration"); }, icon: Building2 },
    { key: "restaurant", get label() { return i18n.t("admin.l.Restaurant_Configuration", "Restaurant Configuration"); }, icon: Store },
    { key: "audit", get label() { return i18n.t("admin.l.Audit_Log", "Audit Log"); }, icon: History },
];

export default function AdminConfiguration() {
    const [sub, setSub] = useState("platform");
    return (
        <div className="space-y-6" data-testid="admin-configuration">
            <div className="flex items-center gap-3 border-b border-neutral-200 dark:border-neutral-800">
                {SUB_TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button key={t.key}
                            onClick={() => setSub(t.key)}
                            data-testid={`config-subtab-${t.key}`}
                            className={"flex items-center gap-2 px-3 py-2 text-sm border-b-2 -mb-px transition-colors " +
                                (sub === t.key
                                    ? "border-black dark:border-white font-medium"
                                    : "border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200")}>
                            <Icon className="w-4 h-4" />{t.label}
                        </button>
                    );
                })}
            </div>
            <div className="rounded-md bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 px-4 py-2 text-xs text-neutral-500 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                {i18n.t("admin.l.Super_Admin_only_All_changes_are_recorded_in_the_audit_log", "Super Admin only. All changes are recorded in the audit log.")}
            </div>
            {sub === "platform" && <PlatformDefaultPanel />}
            {sub === "company" && <CompanyConfigList />}
            {sub === "restaurant" && <RestaurantConfigList />}
            {sub === "audit" && <AuditLogPanel />}
        </div>
    );
}
